import {Component, OnDestroy, OnInit, ViewChild, ViewChildren} from '@angular/core';
import {KnockoutFieldComponent} from "../../../../core/components/knockout-field/knockout-field.component";
import {KnockoutFieldModel} from "../../../../core/components/knockout-field/knockout-field.model";
import {Subject, Subscription} from "rxjs";
import {Location} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {RequestService} from "../../../../core/services/request/request.service";
import {LegacyNavigateService} from "../../../../core/services/legacy-navigate/legacy-navigate.service";
import {ProgressDialogComponent} from "../../../../core/components/progress-dialog/progress-dialog.component";
import {InternationalizationService} from "../../../../core/services/internationalization/internationalization.service";

enum ImportDataMode {
  ImportData,
  ImportDataSuccess,
}

@Component({
  selector: 'sb-import-data',
  templateUrl: './import-data.component.html',
  styleUrls: ['./import-data.component.scss']
})
export class ImportDataComponent implements OnInit, OnDestroy {

  // Progress dialog

  progressTitle = "Importing data";
  progressTopic = "import_controller.submit";
  progressAutoClose = true;
  @ViewChild("progressDialog") progressDialog: ProgressDialogComponent;

  @ViewChildren(KnockoutFieldComponent)
  knockoutFields: KnockoutFieldComponent[];

  ImportDataMode = ImportDataMode; // Needed to access enum in the template

  page: ImportDataMode = ImportDataMode.ImportData;

  validationFailed: boolean = false;

  doUploadCreateTable = true;
  doUploadNewFile = true;
  createTableTableName;
  createTableSheet;
  createTableHeaderRow;
  existingImportTable;
  existingImportSource;
  fileUploadAttachmentId = null;
  importImportTransform;
  importImportLoadType;

  // TODO use these to get the file upload component to have the correct settings when rebuilt
  fileUploadFileName;
  fileUploadMimeType;

  responseData;

  // Set up a mechanism for us to communicate with the child components (e.g. to ask for validation)
  eventsSubject: Subject<any> = new Subject<any>();

  importLogTableRadio: KnockoutFieldModel = {
    name: 'Boolean',
    i18nKey: "Which staging table?",
    datatype: 'boolean',
    availableValues: [],
    referenceTable: null,
    readonly: false,
    mandatory: false,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "switch",
    rendererProperties: {labelTrue: 'Create table', labelFalse: 'Existing table'},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.doUploadCreateTable = value;
      }
    },
  };

  importLogName: KnockoutFieldModel = {
    name: 'String',
    i18nKey: "Staging table name",
    datatype: 'text',
    availableValues: [],
    referenceTable: null,
    readonly: false,
    mandatory: true,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "textfield",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.createTableTableName = value;
      }
    },
    keyupEventCallback: (fieldViewModel, event) => {
      const value = event.target.value;
      this.createTableTableName = value;
    }
  };

  importLogTableList: KnockoutFieldModel = {
    name: "ImportTable",
    i18nKey: "Staging table",
    datatype: 'reference',
    availableValues: [],
    referenceTable: "ImportTable",
    readonly: false,
    mandatory: true,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "onetoone",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.existingImportTable = value;
      }
    },
  };

  importSourceRadio: KnockoutFieldModel = {
    name: 'Boolean',
    i18nKey: 'Which import source?',
    datatype: 'boolean',
    availableValues: [],
    referenceTable: null,
    readonly: false,
    mandatory: false,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "switch",
    rendererProperties: {labelTrue: 'New file', labelFalse: 'Existing import source'},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.doUploadNewFile = value;
      }
    },
  };

  importSourceUpload: KnockoutFieldModel = {
    name: "Attachment",
    i18nKey: "Upload file",
    datatype: 'reference',
    availableValues: [],
    referenceTable: "Attachment",
    readonly: false,
    mandatory: true,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: this.fileUploadAttachmentId ? {
        value: this.fileUploadAttachmentId,
        displayValue: "what should this be"
      } : null,// null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: null, // this.newAttachment ? { value: this.newAttachment, displayValue: "bilbo" } : null, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "fileUpload",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.fileUploadAttachmentId = value;
      }
    },
    onSuccessCallback: (value, file) => {
      this.fileUploadFileName = file.name;
      this.fileUploadMimeType = file.type;
      this.fileUploadAttachmentId = value;
      // TODO assign these into the correct place for the file upload renderer to get them
    }
  };

  importSourceExistingSource: KnockoutFieldModel = {
    name: "ImportSource",
    i18nKey: "Import source",
    datatype: 'reference',
    availableValues: [],
    referenceTable: "ImportSource",
    readonly: false,
    mandatory: true,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "onetoone",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.existingImportSource = value;
      }
    },
  };

  importLogSheet: KnockoutFieldModel = {
    name: 'String',
    i18nKey: "XLS sheet",
    datatype: 'text',
    availableValues: [],
    referenceTable: null,
    readonly: false,
    mandatory: false,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "textfield",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.createTableSheet = value;
      }
    },
    keyupEventCallback: (fieldViewModel, event) => {
      const value = event.target.value;
      this.createTableSheet = value;
    }
  };

  importLogHeaderRow: KnockoutFieldModel = {
    name: 'String',
    i18nKey: "CSV/XLS header row",
    datatype: 'text',
    availableValues: [],
    referenceTable: null,
    readonly: false,
    mandatory: false,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "textfield",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.createTableHeaderRow = value;
      }
    },
    keyupEventCallback: (fieldViewModel, event) => {
      const value = event.target.value;
      this.createTableHeaderRow = value;
    }
  };

  importLoadType: KnockoutFieldModel = {
    name: "ImportLoadType",
    i18nKey: "Import load type",
    datatype: 'choice',
    availableValues: [ // Why does this need displayValue: AND i18nKey: ?
      {value: 'create', displayValue: 'Create', i18nKey: 'Create'},
      {value: 'update', displayValue: 'Update', i18nKey: 'Update'},
      {value: 'delete', displayValue: 'Delete', i18nKey: 'Delete'},
      {value: 'sync', displayValue: 'Sync', i18nKey: 'Sync'},
    ],
    referenceTable: null,
    readonly: false,
    mandatory: true,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: {value: 'sync', displayValue: 'Sync', i18nKey: 'Sync'},
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: null, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "choice",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.importImportLoadType = value;
      }
    },
  };

  importTransformTableList: KnockoutFieldModel = {
    name: "ImportTransform",
    i18nKey: "Import transform",
    datatype: 'reference',
    availableValues: [],
    referenceTable: "ImportTransform",
    readonly: false,
    mandatory: false,
    enablePreview: false,
    tooltip: null,
    data: {
      defaultValue: null, // null | {value?: string, displayValue?: string };
      defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
      answer: {
        value: null,
        displayValue: null
      }, // null | {value?: string, displayValue?: string };
    },
    requestId: null,
    table: 'SyntheticTable',
    renderer: "onetoone",
    rendererProperties: {},
    valueChangeCallback: (value) => {
      if (value != undefined) {
        this.importImportTransform = value;
      }
    },
  };


  constructor(private location: Location,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private requestService: RequestService,
              private legacyNavigateService: LegacyNavigateService,
              private i18nService: InternationalizationService) { }

  ngOnInit(): void {
    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }

    // Mark as import page
    document.getElementById("detailMain")?.classList?.add("sb-import-page-content");
  }

  ngOnDestroy() {
    document.getElementById("detailMain")?.classList?.remove("sb-import-page-content");
  }

  async submitImportData() {

    this.validationFailed = false;

    await this.eventsSubject.next("validate");

    /* Doing it this way would be nice, but the attachment field doesn't answer validated() correctly */
    // if (this.knockoutFields.length > 0 && this.knockoutFields.map((f) => f.koFieldModel.value.validated()).includes(false)) {
    //   this.validationFailed = true;
    // }

    if (this.doUploadCreateTable && !this.createTableTableName) {
      this.validationFailed = true;
    }

    if (!this.doUploadCreateTable && !this.existingImportTable) {
      this.validationFailed = true;
    }

    if (this.doUploadNewFile && !this.fileUploadAttachmentId) {
      this.validationFailed = true;
    }

    if (!this.doUploadNewFile && !this.existingImportSource) {
      this.validationFailed = true;
    }

    if (!this.validationFailed) {

      this.progressDialog.showDialog();

      const data = {
        doUploadCreateTable: this.doUploadCreateTable,
        doUploadNewFile: this.doUploadNewFile,
        createTableTableName: this.createTableTableName,
        existingImportTable: this.existingImportTable,
        existingImportSource: this.existingImportSource,
        fileUploadAttachmentId: this.fileUploadAttachmentId,
        createTableSheet: this.createTableSheet,
        createTableHeaderRow: this.createTableHeaderRow,
        importLoadType: this.importImportLoadType,
        importTransform: this.importImportTransform
      };

      try {
        const response: any = await this.requestService.createRequest()
          .controller("servicely.core.Import")
          .method("submitImport")
          .data(data)
          .execute();

        this.responseData = response.data;

        if (this.responseData && this.responseData.success) {
          this.page = ImportDataMode.ImportDataSuccess;
        }
      } catch (e) {
        this.responseData = { success: false, error: e };
      } finally {
        this.progressDialog.hideDialog();
      }
    }
  }

  toggleExpanded(e: MouseEvent) {
    const ancestor = (e.target as Element).closest(".layout-element-wrapper");
    const collapsedElements = ancestor.querySelectorAll(".is-collapsed");
    const expandedElements = ancestor.querySelectorAll(".is-expanded");

    collapsedElements.forEach((e) => {
      e.classList.remove("is-collapsed");
      e.classList.add("is-expanded");
    });

    expandedElements.forEach((e) => {
      e.classList.remove("is-expanded");
      e.classList.add("is-collapsed");
    });

  }

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

}
